/**
 * @voltxampere/engine — Gmin stepping fallback (SPECS §3.6)
 *
 * When Newton fails from a cold start we re-run with a regularising
 * conductance `gmin` added between every node and ground, sweeping
 * from large (1e-1, almost a short-to-ground) to small (1e-12,
 * essentially absent). Each level warm-starts from the previous
 * level's converged iterate. After the final step we run one more
 * Newton with `gmin = 0` to recover the un-regularised answer.
 *
 * The sweep follows the SPECS §3.6 sequence verbatim. Failure at
 * any non-final level aborts the fallback (caller falls through to
 * source stepping); failure at the final `gmin = 0` step is the
 * authoritative "nope" — the solver throws.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { ConvergenceError } from "@voltxampere/shared";
import { newtonRaphson } from "./newton-raphson.js";
/** SPECS §3.6: log-decade sweep, large to small. */
export const GMIN_STEPPING_SCHEDULE = [
    1e-1, 1e-2, 1e-3, 1e-4, 1e-5, 1e-6, 1e-7, 1e-8, 1e-9, 1e-10, 1e-11, 1e-12,
];
export function gminStepping(circuit, plan, opts) {
    const schedule = opts.schedule ?? GMIN_STEPPING_SCHEDULE;
    let x = new Float64Array(plan.size); // cold start at zero
    let totalIterations = 0;
    let totalClampEvents = 0;
    let totalArmijoEvents = 0;
    const stepsConverged = [];
    for (const gmin of schedule) {
        const r = newtonRaphson(circuit, plan, {
            maxIterations: opts.maxIterations,
            reltol: opts.reltol,
            abstol: opts.abstol,
            x0: x,
            gmin,
            ...(opts.enableArmijo !== undefined ? { enableArmijo: opts.enableArmijo } : {}),
        });
        totalIterations += r.iterations;
        totalClampEvents += r.clampEvents;
        totalArmijoEvents += r.armijoEvents;
        if (!r.converged) {
            // Sprint 5.1 P0 #3: report the *accumulated* iteration count
            // (every inner Newton call up to and including this failed
            // level), not just the failing call's count. The DCSolver
            // orchestrator reads `meta.iterations` to populate
            // `DCResult.iterations`; pre-fix it lost every successful
            // level's contribution on a fallback failure.
            throw new ConvergenceError(`gmin stepping failed at gmin=${String(gmin)} after ${String(r.iterations)} iterations`, {
                meta: {
                    gmin,
                    iterations: totalIterations,
                    stepsConverged: [...stepsConverged],
                    fallback: "gmin",
                },
            });
        }
        stepsConverged.push(gmin);
        x = r.x;
    }
    // Final tightness check at gmin = 0 to drop the regularisation.
    const final = newtonRaphson(circuit, plan, {
        maxIterations: opts.maxIterations,
        reltol: opts.reltol,
        abstol: opts.abstol,
        x0: x,
        gmin: 0,
        ...(opts.enableArmijo !== undefined ? { enableArmijo: opts.enableArmijo } : {}),
    });
    totalIterations += final.iterations;
    totalClampEvents += final.clampEvents;
    totalArmijoEvents += final.armijoEvents;
    if (!final.converged) {
        throw new ConvergenceError(`gmin stepping reached gmin=0 but final Newton did not converge (${String(final.iterations)} iterations)`, {
            meta: {
                gmin: 0,
                iterations: totalIterations,
                stepsConverged: [...stepsConverged],
                fallback: "gmin",
            },
        });
    }
    return { result: final, totalIterations, totalClampEvents, totalArmijoEvents, stepsConverged };
}
//# sourceMappingURL=gmin-stepping.js.map